/**
 * A deterministic, presentation-agnostic runner for one authored story beat.
 *
 * Stages only receive simulation facts and an emitter. Keeping this seam free
 * of game objects means the exact same beat can be replayed in Node when a
 * player reports an ordering failure from a fixed simulation step.
 */

import type { Beat, Cue, Emit, Stage, StoryContext } from '@/story/types';

type StoryInput = Omit<StoryContext, 'beatTime' | 'stageTime'>;

type LastInput = {
  completed: ReadonlySet<string>;
  progress: ReadonlyMap<string, number>;
  self: { readonly x: number; readonly y: number; readonly z: number };
};

/**
 * The few mechanical limits that protect the fixed simulation loop.
 * Comments live beside these values because changing either changes how an
 * authored beat fails under pressure, rather than merely changing an algorithm.
 */
export const STORY_TUNING = {
  /** More than eight instant stages in one tick is bad authored content; never let it hang the sim. */
  maxTransitionsPerUpdate: 8,
  /** A forgotten consumer must lose old presentation cues, not grow the simulation's memory forever. */
  cueQueueCap: 256,
} as const;

export class BeatSequencer {
  private readonly beat: Beat;
  private lastInput: LastInput = {
    completed: new Set<string>(),
    progress: new Map<string, number>(),
    self: { x: 0, y: 0, z: 0 },
  };
  private readonly cues: Cue[] = [];
  private index = 0;
  private hasEnteredCurrent = false;
  private isFinished = false;
  private stageTime = 0;
  private beatTime = 0;
  private cuesEmitted = 0;

  constructor(beat: Beat) {
    this.beat = beat;
    // Fail before the first tick, not at `skipTo`. `skipTo`'s `findIndex` picks
    // the FIRST matching id, so a duplicate stage id does not throw there — it
    // silently makes every id after the first one unreachable by name, and an
    // empty id is indistinguishable from "no stage matched" in the same call.
    // Catching both here means a malformed beat definition never gets as far as
    // running content instead of the mistake it actually is.
    const seenAt = new Map<string, number>();
    beat.stages.forEach((stage, index) => {
      if (stage.id === '') {
        throw new Error(`beat ${beat.number} (${beat.id}): empty stage id at index ${index}`);
      }
      const firstIndex = seenAt.get(stage.id);
      if (firstIndex !== undefined) {
        throw new Error(
          `beat ${beat.number} (${beat.id}): duplicate stage id "${stage.id}" at indices ${firstIndex} and ${index}`,
        );
      }
      seenAt.set(stage.id, index);
    });

    this.isFinished = beat.stages.length === 0;
  }

  get beatNumber(): number {
    return this.beat.number;
  }

  get stageId(): string | null {
    return this.currentStage()?.id ?? null;
  }

  get stageIndex(): number {
    return this.index;
  }

  get finished(): boolean {
    return this.isFinished;
  }

  get debug(): {
    stageId: string | null;
    stageIndex: number;
    stageTime: number;
    beatTime: number;
    cuesEmitted: number;
  } {
    return {
      stageId: this.stageId,
      stageIndex: this.stageIndex,
      stageTime: this.stageTime,
      beatTime: this.beatTime,
      cuesEmitted: this.cuesEmitted,
    };
  }

  /** Runs on the fixed simulation step. */
  update(dt: number, ctx: StoryInput): void {
    if (this.isFinished) return;

    this.lastInput.completed = ctx.completed;
    this.lastInput.progress = ctx.progress;
    this.lastInput.self = ctx.self;

    this.enterCurrent();
    const transitions = this.advanceCompleted(STORY_TUNING.maxTransitionsPerUpdate);
    if (this.isFinished) return;

    const safeDt = Number.isFinite(dt) && dt > 0 ? dt : 0;
    this.beatTime += safeDt;
    this.stageTime += safeDt;

    const stage = this.currentStage();
    if (stage === undefined) return;
    const stageContext = this.context();
    stage.update?.(safeDt, stageContext, this.emitter(stageContext));

    // `done` remains observable after every update even when this tick already
    // spent its transition budget. The next fixed step will make the deferred
    // transition, so an authored runaway cannot stall the sim loop.
    this.advanceCompleted(STORY_TUNING.maxTransitionsPerUpdate - transitions);
  }

  /** Takes the cues emitted since the last call and clears the queue. */
  drainCues(): Cue[] {
    const drained = this.cues.slice();
    this.cues.length = 0;
    return drained;
  }

  /**
   * Debug and test seam: visit authored stages without evaluating completion.
   * Rewinding replays from the first stage because a stage may have emitted a
   * cue on entry; simply changing the index once left test replays with a
   * different cue trace than the corresponding forward run.
   */
  skipTo(stageId: string): void {
    const targetIndex = this.beat.stages.findIndex((stage) => stage.id === stageId);
    if (targetIndex === -1) throw new Error(`Unknown story stage: ${stageId}`);

    if (targetIndex < this.index || this.isFinished) {
      const current = this.currentStage();
      if (current !== undefined && this.hasEnteredCurrent) {
        const stageContext = this.context();
        current.exit?.(stageContext, this.emitter(stageContext));
      }
      this.index = 0;
      this.hasEnteredCurrent = false;
      this.isFinished = false;
      this.stageTime = 0;
    }

    this.enterCurrent();
    while (this.index < targetIndex) {
      const stage = this.currentStage();
      if (stage === undefined) return;
      const stageContext = this.context();
      stage.exit?.(stageContext, this.emitter(stageContext));
      this.index += 1;
      this.hasEnteredCurrent = false;
      this.enterCurrent();
    }
  }

  private currentStage(): Stage | undefined {
    if (this.isFinished) return undefined;
    return this.beat.stages[this.index];
  }

  private context(): StoryContext {
    return {
      beatTime: this.beatTime,
      stageTime: this.stageTime,
      completed: this.lastInput.completed,
      progress: this.lastInput.progress,
      self: this.lastInput.self,
    };
  }

  private emitter(ctx: StoryContext): Emit {
    return (id, data): void => {
      const cue = data === undefined ? { id, at: ctx.beatTime } : { id, at: ctx.beatTime, data };
      this.cues.push(cue);
      this.cuesEmitted += 1;
      if (this.cues.length > STORY_TUNING.cueQueueCap) {
        // Presentation can recover from an old missed cue; the simulation cannot
        // recover from an unbounded queue that eventually stalls its fixed step.
        this.cues.shift();
      }
    };
  }

  private enterCurrent(): void {
    if (this.hasEnteredCurrent || this.isFinished) return;
    const stage = this.currentStage();
    if (stage === undefined) {
      this.isFinished = true;
      return;
    }
    this.hasEnteredCurrent = true;
    this.stageTime = 0;
    const stageContext = this.context();
    stage.enter?.(stageContext, this.emitter(stageContext));
  }

  private advanceCompleted(limit: number): number {
    let transitions = 0;
    while (!this.isFinished && transitions < limit) {
      this.enterCurrent();
      const stage = this.currentStage();
      if (stage === undefined || !stage.done(this.context())) break;

      const stageContext = this.context();
      stage.exit?.(stageContext, this.emitter(stageContext));
      this.index += 1;
      this.hasEnteredCurrent = false;
      transitions += 1;

      if (this.index === this.beat.stages.length) {
        this.isFinished = true;
        break;
      }
      this.enterCurrent();
    }

    // There was an extra `currentStage()?.done(...)` here whose result was
    // discarded, commented as "intentionally an observation only". It was
    // removed: `done` is authored content, nothing in its type stops it having a
    // side effect, and a call made for no reason is the kind of thing that
    // eventually gets one. The deferred transition happens on the next fixed
    // step either way, which is the whole point of the cap.
    return transitions;
  }
}
